import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ASSETS } from "@/data/assets";
import { scrollToId } from "@/hooks/useLenis";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageSelector } from "./LanguageSelector";

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const NAV = [
    { label: t.nav.catalogue, id: "catalogue" },
    { label: t.nav.researchBox, id: "research-box" },
    { label: t.nav.about, id: "about" },
    { label: t.nav.contact, id: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between">
        <button
          onClick={() => go("hero")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <motion.div
            initial={{ scale: 0.12, opacity: 0, filter: "blur(10px)" }}
            animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
            transition={{ duration: 1.4, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <img src={ASSETS.logo} alt="AlloLabs UK" className="logo-glow h-9 w-auto object-contain" />
          </motion.div>
          <span className="hidden sm:block font-cinzel font-bold tracking-wide text-[15px] text-white">
            AlloLabs <span className="text-[var(--al-gold)]">UK</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              className="gold-underline font-body text-[13px] tracking-[0.18em] uppercase text-white/70 hover:text-white transition-colors duration-300"
              data-testid={`nav-${n.id}`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <button
            onClick={() => go("contact")}
            className="btn-gold border border-[var(--al-gold)]/60 px-5 py-2.5 text-[12px] tracking-[0.2em] uppercase font-body text-[var(--al-gold)]"
            data-testid="nav-inquire-btn"
          >
            <span>{t.nav.requestQuote}</span>
          </button>
          <LanguageSelector />
        </div>

        <div className="md:hidden flex items-center gap-3">
          <LanguageSelector compact />
          <button
            className="text-white flex flex-col gap-[5px] p-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            <span className={`h-[1.5px] w-6 bg-white transition-transform duration-300 ${open ? "translate-y-[6.5px] rotate-45" : ""}`} />
            <span className={`h-[1.5px] w-6 bg-white transition-opacity duration-300 ${open ? "opacity-0" : ""}`} />
            <span className={`h-[1.5px] w-6 bg-white transition-transform duration-300 ${open ? "-translate-y-[6.5px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-black/90 backdrop-blur-2xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(n.id)}
                  className="text-left font-body text-sm tracking-[0.18em] uppercase text-white/80"
                  data-testid={`nav-mobile-${n.id}`}
                >
                  {n.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
